import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, X, Loader2 } from "lucide-react";

/**
 * Tek veya çoklu resim yükleme. `value` bir url dizisi (multiple=true) ya da
 * tek bir url string'i (multiple=false) olur. Yükleme /memur/uploads'a gider,
 * dönen url onChange ile üst bileşene iletilir.
 */
export default function ImageUploader({ value, onChange, multiple = true, label = "Resim Ekle" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const urls = multiple ? value || [] : (value ? [value] : []);

  const handleFiles = async (files) => {
    setUploading(true);
    try {
      for (const file of files) {
        const form = new FormData();
        form.append("file", file);
        const { data } = await api.post("/memur/uploads", form);
        if (multiple) {
          onChange([...(value || []), data.url]);
        } else {
          onChange(data.url);
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Resim yüklenemedi");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const removeAt = (idx) => {
    if (multiple) onChange(urls.filter((_, i) => i !== idx));
    else onChange("");
  };

  return (
    <div className="space-y-2">
      <input ref={inputRef} type="file" accept="image/*" multiple={multiple} className="hidden"
        data-testid="image-upload-input"
        onChange={(e) => e.target.files?.length && handleFiles(Array.from(e.target.files))} />
      <Button type="button" size="sm" variant="outline" disabled={uploading || (!multiple && urls.length > 0)}
        onClick={() => inputRef.current?.click()} data-testid="image-upload-trigger"
        className="border-zinc-700">
        {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
        {label}
      </Button>
      {urls.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {urls.map((u, i) => (
            <div key={u + i} className="relative w-20 h-20 border border-zinc-800 overflow-hidden group">
              <img src={u} alt="" className="w-full h-full object-cover" />
              <button type="button" onClick={() => removeAt(i)} data-testid={`image-remove-${i}`}
                className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                <X className="w-5 h-5 text-rose-400" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
